<template>
  <div class="video-manage">
    <div class="page-header">
      <h2>学习内容管理</h2>
      <p>管理视频、PDF等学习内容，支持增删改查操作</p>
    </div>

    <el-card class="filter-card">
      <div class="filter-actions">
        <el-button type="primary" icon="el-icon-plus" @click="handleAdd">
          新增内容
        </el-button>
        <el-button type="info" icon="el-icon-refresh" @click="refresh">
          刷新
        </el-button>
      </div>
    </el-card>

    <el-card>
      <el-table :data="videoList" border style="width: 100%" v-loading="loading">
        <el-table-column prop="id" label="ID" width="80" align="center"></el-table-column>
        <el-table-column label="栏目" width="120" align="center">
          <template slot-scope="scope">
            <el-tag type="info" size="small">{{ getCategoryName(scope.row.category_id) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="title" label="标题" min-width="200"></el-table-column>
        <el-table-column label="类型" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.type === 'pdf' ? 'warning' : 'primary'" size="small">
              {{ scope.row.type === 'pdf' ? 'PDF' : '视频' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="描述" min-width="200">
          <template slot-scope="scope">
            {{ scope.row.description || scope.row.desc }}
          </template>
        </el-table-column>
        <el-table-column label="内容地址" min-width="180">
          <template slot-scope="scope">
            <el-button
              v-if="scope.row.videoUrl || (scope.row.pdfImages && scope.row.pdfImages.length > 0)"
              type="text"
              @click="viewContent(scope.row)"
            >
              查看内容
            </el-button>
            <span v-else class="no-url">暂无</span>
          </template>
        </el-table-column>
        <el-table-column label="创建时间" width="160">
          <template slot-scope="scope">
            {{ scope.row.created_at || scope.row.createdAt }}
          </template>
        </el-table-column>
        <el-table-column label="更新时间" width="160">
          <template slot-scope="scope">
            {{ scope.row.updated_at || scope.row.updatedAt }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button
              type="primary"
              icon="el-icon-edit"
              size="mini"
              @click="handleEdit(scope.row)"
            >编辑</el-button>
            <el-button
              type="danger"
              icon="el-icon-delete"
              size="mini"
              @click="handleDelete(scope.row.id)"
            >删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 选择内容类型对话框 -->
    <el-dialog
      title="选择内容类型"
      :visible.sync="typeDialogVisible"
      width="400px"
      center
    >
      <div class="type-selector">
        <div class="type-option" @click="selectType('video')">
          <i class="el-icon-video-camera"></i>
          <span>视频内容</span>
          <p>上传或添加视频链接</p>
        </div>
        <div class="type-option" @click="selectType('pdf')">
          <i class="el-icon-document"></i>
          <span>PDF内容</span>
          <p>上传或添加PDF文档链接</p>
        </div>
      </div>
    </el-dialog>

    <!-- PDF图片预览对话框 -->
    <el-dialog
      title="PDF内容预览"
      :visible.sync="pdfPreviewVisible"
      width="800px"
      class="pdf-preview-dialog"
    >
      <div class="pdf-images-container" v-if="pdfPreviewImages.length > 0">
        <div class="image-wrapper" v-for="(img, index) in pdfPreviewImages" :key="index">
          <el-image
            :src="getFullUrl(img)"
            :preview-src-list="pdfPreviewImages.map(i => getFullUrl(i))"
            fit="contain"
            lazy
          />
          <div class="page-number">第 {{ index + 1 }} 页</div>
        </div>
      </div>
      <div v-else class="no-images">暂无图片</div>
    </el-dialog>

    <!-- 新增/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="500px"
      @close="resetForm"
    >
      <el-form
        ref="videoForm"
        :model="formData"
        :rules="formRules"
        label-width="80px"
      >
        <el-form-item label="所属栏目" prop="categoryId">
          <el-select v-model="formData.categoryId" placeholder="请选择栏目" style="width: 100%">
            <el-option
              v-for="cat in categories"
              :key="cat.id"
              :label="cat.name"
              :value="cat.id"
            ></el-option>
          </el-select>
        </el-form-item>

        <el-form-item label="标题" prop="title">
          <el-input
            v-model="formData.title"
            placeholder="请输入标题"
            maxlength="50"
            show-word-limit
          />
        </el-form-item>

        <el-form-item label="描述" prop="desc">
          <el-input
            v-model="formData.desc"
            type="textarea"
            :rows="3"
            placeholder="请输入描述"
            maxlength="200"
            show-word-limit
          />
        </el-form-item>
        
        <el-form-item label="内容" prop="videoUrl">
          <el-radio-group v-model="formData.videoType" style="margin-bottom: 10px;">
            <el-radio label="url">URL地址</el-radio>
            <el-radio label="file">本地上传</el-radio>
          </el-radio-group>

          <el-input
            v-if="formData.videoType === 'url'"
            v-model="formData.videoUrl"
            :placeholder="contentType === 'pdf' ? '请输入PDF文档链接' : '请输入视频地址'"
          />

          <el-upload
            v-else
            class="upload-area"
            :action="uploadUrl"
            :headers="uploadHeaders"
            :on-success="handleVideoSuccess"
            :on-error="handleVideoError"
            :on-progress="handleVideoProgress"
            :show-file-list="false"
            :accept="contentType === 'pdf' ? '.pdf' : 'video/*'"
            :disabled="videoUploading"
          >
            <el-button size="small" type="primary" :loading="videoUploading">
              {{ videoUploading ? '上传中 ' + videoProgress + '%' : (contentType === 'pdf' ? '选择PDF文件' : '选择视频') }}
            </el-button>
            <div v-if="formData.videoUrl && !videoUploading" class="preview-url">
              {{ formData.videoUrl }}
            </div>
            <el-progress v-if="videoUploading" :percentage="videoProgress" :stroke-width="8" style="margin-top: 8px;" :format="formatProgress"></el-progress>
          </el-upload>
        </el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">
          确认
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import config from '@/config'

export default {
  name: 'VideoManagement',
  data() {
    return {
      loading: false,
      dialogVisible: false,
      typeDialogVisible: false,
      pdfPreviewVisible: false,
      pdfPreviewImages: [],
      submitting: false,
      isEdit: false,
      currentId: null,
      contentType: 'video',
      uploadUrl: config.apiBaseUrl + '/upload',
      uploadHeaders: {
        Authorization: `Bearer ${localStorage.getItem('admin-token')}`
      },
      staticUrl: config.staticUrl,
      videoUploading: false,
      videoProgress: 0,
      formData: {
        categoryId: null,
        title: '',
        desc: '',
        videoUrl: '',
        type: 'video',
        videoType: 'url',
        pdfImages: []
      },
      formRules: {
        title: [
          { required: true, message: '请输入标题', trigger: 'blur' }
        ],
        desc: [
          { required: true, message: '请输入描述', trigger: 'blur' }
        ],
        videoUrl: [
          { required: true, message: '请输入内容地址', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    ...mapState(['videos', 'categories']),
    videoList() {
      return this.videos
    },
    dialogTitle() {
      const typeText = this.contentType === 'pdf' ? 'PDF内容' : '视频内容'
      return this.isEdit ? `编辑${typeText}` : `新增${typeText}`
    }
  },
  async created() {
    await this.loadData()
  },
  methods: {
    ...mapActions(['addVideo', 'updateVideo', 'deleteVideo', 'fetchVideos']),
    getFullUrl(url) {
      if (!url) return ''
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url
      }
      return this.staticUrl + url
    },
    async loadData() {
      this.loading = true
      try {
        await Promise.all([
          this.fetchVideos(),
          this.$store.dispatch('fetchCategories')
        ])
      } catch (error) {
        this.$message.error('加载内容列表失败：' + error.message)
      } finally {
        this.loading = false
      }
    },
    getCategoryName(categoryId) {
      const cat = this.categories.find(c => c.id === categoryId)
      return cat ? cat.name : '未分类'
    },
    async refresh() {
      await this.loadData()
      this.$message.success('刷新成功')
    },
    handleAdd() {
      this.isEdit = false
      this.typeDialogVisible = true
    },
    viewContent(row) {
      // 如果是PDF类型且有转换后的图片，显示图片预览
      if (row.type === 'pdf' && row.pdfImages && row.pdfImages.length > 0) {
        this.pdfPreviewImages = row.pdfImages
        this.pdfPreviewVisible = true
      } else {
        // 视频类型或PDF无图片，直接打开链接
        const url = this.getFullUrl(row.videoUrl || row.video_url)
        if (url) {
          window.open(url, '_blank')
        }
      }
    },
    selectType(type) {
      this.contentType = type
      this.formData.type = type
      this.typeDialogVisible = false
      this.dialogVisible = true
    },
    handleEdit(row) {
      this.isEdit = true
      this.currentId = row.id
      this.contentType = row.type || 'video'
      this.formData = {
        categoryId: row.category_id || row.categoryId || null,
        title: row.title,
        desc: row.description || row.desc,
        videoUrl: row.video_url || row.videoUrl,
        type: row.type || 'video',
        videoType: 'url',
        pdfImages: row.pdf_images || row.pdfImages || []
      }
      this.dialogVisible = true
    },
    handleVideoSuccess(response) {
      this.videoUploading = false
      this.videoProgress = 0
      if (response.code === 200) {
        this.formData.videoUrl = response.data.url
        if (response.data.pdf_images && response.data.pdf_images.length > 0) {
          this.formData.pdfImages = response.data.pdf_images
          this.$message.success(`内容上传成功，已转换为${response.data.page_count}张图片`)
        } else {
          this.$message.success('内容上传成功')
        }
      } else {
        this.$message.error(response.message || '上传失败')
      }
    },
    handleVideoProgress(event) {
      this.videoUploading = true
      this.videoProgress = Math.round(event.percent || 0)
    },
    handleVideoError() {
      this.videoUploading = false
      this.videoProgress = 0
      this.$message.error('内容上传失败，请检查网络连接')
    },
    handleUploadError() {
      this.$message.error('上传失败，请检查网络连接')
    },
    formatProgress(percentage) {
      return percentage + '%'
    },
    async handleDelete(id) {
      try {
        await this.$confirm('确定要删除这个内容吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        this.deleteVideo(id)
        this.$message.success('删除成功')
      } catch {
        // 用户取消了删除
      }
    },
    submitForm() {
      this.$refs.videoForm.validate(async valid => {
        if (valid) {
          this.submitting = true

          try {
            // 转换字段名为后端期待的格式
            const submitData = {
              title: this.formData.title,
              description: this.formData.desc,
              category_id: this.formData.categoryId,
              video_url: this.formData.videoUrl,
              type: this.formData.type
            }

            // 如果是PDF且有转换后的图片，添加到提交数据
            if (this.formData.type === 'pdf' && this.formData.pdfImages.length > 0) {
              submitData.pdf_images = this.formData.pdfImages
            }

            if (this.isEdit) {
              await this.updateVideo({
                id: this.currentId,
                video: submitData
              })
              this.$message.success('更新成功')
            } else {
              await this.addVideo(submitData)
              this.$message.success('新增成功')
            }

            this.dialogVisible = false
            this.resetForm()
          } finally {
            this.submitting = false
          }
        }
      })
    },
    resetForm() {
      if (this.$refs.videoForm) {
        this.$refs.videoForm.resetFields()
      }
      this.isEdit = false
      this.currentId = null
      this.contentType = 'video'
      this.videoUploading = false
      this.videoProgress = 0
      this.formData = {
        categoryId: null,
        title: '',
        desc: '',
        videoUrl: '',
        type: 'video',
        videoType: 'url',
        pdfImages: []
      }
    }
  }
}
</script>

<style scoped>
.video-manage {
  padding: 20px;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0 0 8px 0;
  font-size: 24px;
  color: #333;
}

.page-header p {
  margin: 0;
  color: #999;
  font-size: 14px;
}

.filter-card {
  margin-bottom: 20px;
}

.filter-actions {
  display: flex;
  gap: 10px;
}

.form-tip {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.upload-area {
  width: 100%;
}

.preview-url {
  margin-top: 8px;
  font-size: 12px;
  color: #666;
  word-break: break-all;
}

.type-selector {
  display: flex;
  gap: 20px;
  justify-content: center;
}

.type-option {
  flex: 1;
  max-width: 150px;
  padding: 30px 20px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
}

.type-option:hover {
  border-color: #4caf50;
  background-color: #f5f5f5;
}

.type-option i {
  font-size: 48px;
  color: #4caf50;
  margin-bottom: 15px;
  display: block;
}

.type-option span {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  display: block;
  margin-bottom: 8px;
}

.type-option p {
  font-size: 12px;
  color: #999;
  margin: 0;
}

.no-url {
  color: #999;
  font-size: 13px;
}

.pdf-preview-dialog .pdf-images-container {
  max-height: 70vh;
  overflow-y: auto;
}

.pdf-preview-dialog .image-wrapper {
  margin-bottom: 20px;
  text-align: center;
  border-bottom: 1px solid #eee;
  padding-bottom: 15px;
}

.pdf-preview-dialog .image-wrapper:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.pdf-preview-dialog .el-image {
  max-width: 100%;
  max-height: 600px;
}

.pdf-preview-dialog .page-number {
  margin-top: 8px;
  font-size: 12px;
  color: #999;
}

.pdf-preview-dialog .no-images {
  text-align: center;
  padding: 40px;
  color: #999;
}
</style>